import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactQuill from "react-quill";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  DialogActions,
  CircularProgress,
  Box,
  Autocomplete,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import JobModel from "models/jobModel";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { getDepartments } from "services/api";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";

// import { stat } from "fs";

const AddCompetencyDialog = ({
  open,
  onClose,
  competency,
  handleSubmit,
  handleDelete,
  competencies,
  //   companyId,
  //   jobs,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [compName, setCompName] = useState("");
  const [definition, setDefinition] = useState("");
  const [type, setType] = useState(""); // Default is empty
  const [status, setStatus] = useState(""); // Default is empty
  // const [selectedJobId, setSelectedJobId] = useState(null); // Add this state
  const [errors, setErrors] = useState({});
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    if (competency) {
      setCompName(competency.competency_name || "");
      setDefinition(competency.competency_definition || "");
      setType(competency.competency_type || "");
      setStatus(competency.competency_status || "");
      setCompName("");
      setDefinition("");
      setType("");
      setStatus("");
    }
  }, [competency]);

  const validate = () => {
    let newErrors = {};
    // if (!compName.trim()) newErrors.compName = " Competency Name is Required";
    if (!compName.trim()) {
      newErrors.compName = t("admin.competency.validation.nameRequired");
    }
    if (!type.trim()) {
      newErrors.type = t("admin.competency.validation.typeRequired");
    }

    // Check if the name already exists (case-insensitive), excluding the current competency
    const nameExists = competencies.some(
      (comp) => comp.name.toLowerCase() === compName.toLowerCase()
    );
    if (nameExists) {
      newErrors.compName = t("admin.competency.validation.nameExists");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    const competencyData = {
      name: compName,
      definition: definition,
      type: type,
      status: status,
    };
    await handleSubmit(competencyData);
    setLoading(false);
    handleClose();
  };

  const handleClose = () => {
    setCompName("");
    setDefinition("");
    setType("");
    setStatus("");
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        {competency
          ? t("admin.competency.editDialog.edit_competency")
          : t("admin.competency.editDialog.titleAdd")}
      </DialogTitle>
      <DialogContent>
        <TextField
          label={t("admin.competency.editDialog.name") + " *"}
          value={compName}
          onChange={(e) => setCompName(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.compName}
          helperText={errors.compName}
        />
        <Typography variant="h7" sx={{ mt: 3, mb: 2, ml: 1 }}>
          {t("admin.competency.editDialog.definition")}
        </Typography>

        {/* <ReactQuill value={definition} onChange={setDefinition}  style={{ color: "black", fontSize: "16px" }}  /> */}
        <ReactQuill
          value={definition}
          onChange={setDefinition}
          style={{
            color: "black",
            fontSize: "16px",
            maxWidth: "600px", // Limit the max width
            overflowX: "auto", // Enable horizontal scroll if the content exceeds width
            whiteSpace: "pre-wrap", // Ensure text wraps within the editor
            wordBreak: "break-word", // Prevent overflow of long words
          }}
        />

        <TextField
          label={t("admin.competency.editDialog.criteria") + " *"}
          value={type}
          onChange={(e) => setType(e.target.value)}
          select // ✅ Enables dropdown
          fullWidth
          margin="normal"
          error={!!errors.type}
          helperText={errors.type}
          sx={{
            width: "100%", // ✅ Custom width
            "& .MuiInputBase-root": { height: "30px" }, // ✅ Increase field height
            "& .MuiInputBase-input": { fontSize: "0.8rem", padding: "16px" }, // ✅ Bigger text inside
            "& .MuiInputLabel-root": { fontSize: "1.0rem" }, // ✅ Bigger label
            ...fieldStyles,
          }}
        >
          <MenuItem value="Leadership">Leadership</MenuItem>
          <MenuItem value="Technical">Technical</MenuItem>
          <MenuItem value="Core">Core</MenuItem>
        </TextField>
      </DialogContent>
      {!loading ? (
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t("admin.competency.editDialog.cancel")}
          </Button>
          {competency && (
            <Button
              onClick={async () => {
                await handleDelete(competency.id);
                handleClose();
              }}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.competency.editDialog.delete")}
            </Button>
          )}
          <Button
            onClick={handleSave}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {competency
              ? t("admin.competency.editDialog.update")
              : t("admin.competency.editDialog.add")}
          </Button>
        </DialogActions>
      ) : (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      )}
    </Dialog>
  );
};

AddCompetencyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  competency: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  competencies: PropTypes.array.isRequired,
  //   companyId: PropTypes.number,
};

export default AddCompetencyDialog;
